<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
import { formatFullName } from "@/helpers/name";
import { useCompanyNeedOptimistic } from "@/modules/companyNeed/composables/useCompanyNeedOptimistic";
import { getBesoinWorkerSameStatusErrorMessage, isBesoinWorkerSameStatusError } from "@/modules/companyNeed/helpers/besoinWorkerErrors";
import { openLastContactModalForCompanyNeed } from "@/modules/companyNeed/helpers/lastContact";
import { openManageOrderModal } from "@/modules/companyNeed/helpers/manageOrderModal";
import { worklowCreateMissionAddWorkers } from "@/modules/companyNeed/helpers/mission";
import { openScheduleInterviewModal } from "@/modules/companyNeed/helpers/scheduleInterview";
import { mapScheduleInterviewPayloadToWorkerInterview } from "@/modules/companyNeed/helpers/workerInterview";
import companyNeedService from "@/modules/companyNeed/services/companyNeed";
import missionService from "@/modules/companyNeed/services/missionService";
import { useCompanyNeedStore } from "@/modules/companyNeed/store/companyNeedStore";
import { HistoryType } from "@/modules/companyNeed/types/history";
import {
    MISSION_MAX_WORKERS_REACHED_CODE,
    MISSION_WORKER_ALREADY_ASSIGNED_CODE,
    MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE,
    MissionBulkWorkerStatus,
} from "@/modules/companyNeed/types/missions";
import { COMMANDS_REQUIRING_EMAIL, MoveWorkerCompanyNeedCommands, MoveWorkerModalView } from "@/modules/companyNeed/types/moveWorker";
import { CompanyNeedWorkerStatus } from "@/modules/companyNeed/types/worker";

import type { ScheduleInterviewSuccessPayload } from "@/components/modal/Profile/scheduleInterview/types";
import type { CompanyNeed } from "@/modules/companyNeed/types";
import type { MissionBulkWorker } from "@/modules/companyNeed/types/missions";
import type { CompanyNeedActionProgress, CompanyNeedItem } from "@/modules/companyNeed/types/modals";
import type { MoveWorker, MoveWorkerCommand, MoveWorkerCompanyNeedModalProps } from "@/modules/companyNeed/types/moveWorker";
import type { AddWorkersToCompanyNeedResponse, WorkerToAdd } from "@/modules/companyNeed/types/worker";
import type { WorkerToMoveToPool } from "@/modules/companyNeed/views/details/pool/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import QuickActionBadge from "@/dsComponents/QuickActions/QuickActionBadge.vue";
import QuickActionSearch from "@/dsComponents/QuickActions/QuickActionSearch.vue";
import { useQuickActionsNavigation } from "@/dsComponents/QuickActions/useQuickActionsNavigation";

import { ScheduleInterviewType } from "@/components/modal/Profile/scheduleInterview/types";
import CompanyNeedsQuickActionItem from "@/modules/companyNeed/components/modals/quickActions/CompanyNeedsQuickActionItem.vue";
import MissionsQuickActionItem from "@/modules/companyNeed/components/modals/quickActions/MissionsQuickActionItem.vue";
import MoveQuickActionItem from "@/modules/companyNeed/components/modals/quickActions/MoveQuickActionItem.vue";

const { t } = useI18n();
const { toastSuccess, toastError } = useDefaultToast();
const companyNeedStore = useCompanyNeedStore();
const {
    handleMoveToPoolFromRecruitment,
    handleMoveToPoolFromMatching,
    handleMoveFromCandidates,
    reloadAfterPoolMove,
    refreshMatchingIfCurrentPageEmpty,
} = useCompanyNeedOptimistic();
const modalStore = useModalStore();

const props = defineProps<MoveWorkerCompanyNeedModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const handleClose = () => {
    props.onCancel?.();
    emit("close");
};

const currentView = ref(props.initialView ?? MoveWorkerModalView.MOVE);
const search = ref("");
const selectedCompanyNeedId = ref<string | null>(null);
const selectedNeedName = ref<string | null>(null);
const selectedNeedFromJobOffer = ref<Record<string, boolean>>({});
const selectedMissionCommand = ref<MoveWorkerCommand | null>(null);
const actionProgress = ref<CompanyNeedActionProgress>({
    alreadyCompanyNeedIds: [],
    inProgressCompanyNeedId: null,
});

const moveQuickActionItemRef = ref<InstanceType<typeof MoveQuickActionItem> | null>(null);
const missionsQuickActionItemRef = ref<InstanceType<typeof MissionsQuickActionItem> | null>(null);
const companyNeedsQuickActionItemRef = ref<InstanceType<typeof CompanyNeedsQuickActionItem> | null>(null);

const label = computed(() => {
    if (props.workers.length === 1) return formatFullName(props.workers[0]);
    return t("modals.companyNeed.moveWorker.multiProfilesSelected", { count: props.workers.length });
});

const companyNeedId = computed(() => selectedCompanyNeedId.value || props.companyNeedId || "");

const shouldAddProfilesToTargetNeed = computed(() => props.isFromMatching || selectedCompanyNeedId.value !== null);

const isFooterVisible = computed(() => currentView.value === MoveWorkerModalView.MISSIONS && selectedMissionCommand.value !== null);

const isAddToOtherPoolsView = computed(() => currentView.value === MoveWorkerModalView.ADD_TO_OTHER_POOLS);

const searchPlaceholder = computed(() => {
    if (!isAddToOtherPoolsView.value) return t("modals.companyNeed.moveWorker.moveToPlaceholder");
    return props.companyNeedId
        ? t("modals.companyNeed.moveWorker.addToOtherPools.placeholder")
        : t("modals.companyNeed.moveWorker.addToPoolPlaceholder");
});

const searchIcon = computed(() => (isAddToOtherPoolsView.value ? "search" : "arrow-right"));

const workersWithEmail = computed(() => props.workers.filter((worker) => !!worker.email));

const activeQuickActionRef = computed(() => {
    const viewToRef = {
        [MoveWorkerModalView.MOVE]: moveQuickActionItemRef,
        [MoveWorkerModalView.MOVE_TO_ANOTHER_COMPANY_NEED]: moveQuickActionItemRef,
        [MoveWorkerModalView.MISSIONS]: missionsQuickActionItemRef,
        [MoveWorkerModalView.COMPANY_NEEDS]: companyNeedsQuickActionItemRef,
        [MoveWorkerModalView.ADD_TO_OTHER_POOLS]: companyNeedsQuickActionItemRef,
    };
    return viewToRef[currentView.value] ?? null;
});

const { selectedItemIndex, handleKeyDown, updateItemCount, resetSelection } = useQuickActionsNavigation({
    onExecute: (index: number) => {
        activeQuickActionRef.value?.value?.executeSelected(index);
    },
});

watch(currentView, () => {
    resetSelection();
});

const mapMoveWorkerToWorkerToAdd = (worker: MoveWorker): WorkerToAdd => ({
    workerId: worker.id,
    lastname: worker.lastname,
    firstname: worker.firstname,
    workerPhone: worker.phone ?? "",
    workerEmail: worker.email ?? "",
    picture: worker.picture,
    fromJobOffer: selectedNeedFromJobOffer.value[worker.id] || worker.fromJobOffer || props.fromJobOffer,
});

const mapMoveWorkerToWorkerToMoveToPool = (worker: MoveWorker): WorkerToMoveToPool => ({
    workerId: worker.id,
    lastname: worker.lastname,
    firstname: worker.firstname,
    workerPhone: worker.phone ?? "",
    workerEmail: worker.email ?? "",
    picture: worker.picture,
    fromJobOffer: props.fromJobOffer || undefined,
});

const mapMoveWorkerToMissionBulkWorker = (worker: MoveWorker): MissionBulkWorker => ({
    workerId: worker.id,
    firstname: worker.firstname,
    lastname: worker.lastname,
    email: worker.email ?? "",
    phone: worker.phone ?? "",
    fromJobOffer: props.fromJobOffer || undefined,
});

const resolveMoveWorkerErrorMessage = (error: unknown, errorLabel: string, count = props.workers.length): string => {
    if (isBesoinWorkerSameStatusError(error)) {
        return getBesoinWorkerSameStatusErrorMessage(error, count);
    }
    return t(errorLabel);
};

const executeWithFeedback = async <T>(action: () => Promise<T>, succesLabel: string, errorLabel: string): Promise<T | null> => {
    try {
        const response = await action();
        const message = selectedNeedName.value
            ? t("modals.companyNeed.moveWorker.moveToAnotherNeedSuccess", { count: props.workers.length, needName: selectedNeedName.value })
            : succesLabel;
        toastSuccess(message);
        return response;
    } catch (error) {
        toastError(resolveMoveWorkerErrorMessage(error, errorLabel));
        return null;
    }
};

const resolveCompanyNeed = async (): Promise<CompanyNeed | null> => {
    if (companyNeedStore.companyNeed) return companyNeedStore.companyNeed;
    if (!companyNeedId.value) return null;

    try {
        return await companyNeedService.fetchCompanyNeedById(companyNeedId.value);
    } catch {
        toastError(t("modals.companyNeed.moveWorker.updateProfilesStatus.error", { count: props.workers.length }));
        return null;
    }
};

const isJobOfferMove = () => props.fromJobOffer || props.workers.some((worker) => worker.fromJobOffer);

const getJobOfferWorkerIds = () => props.workers.map((worker) => worker.id).filter(Boolean);

const applyOptimisticMoveFromJobOffer = () => {
    if (!isJobOfferMove()) return;
    handleMoveFromCandidates(getJobOfferWorkerIds());
};

const handleOpenManageOrderModal = async () => {
    const eligibleWorkers = workersWithEmail.value;
    if (eligibleWorkers.length === 0) return;

    const need = await resolveCompanyNeed();
    if (!need) return;

    openManageOrderModal(need, {
        onSuccess: async (createdOrder) => {
            if (!createdOrder?.id) return;

            try {
                const { isMissionUpdateSuccessful, alreadyAssignedCount } = await worklowCreateMissionAddWorkers({
                    missionId: createdOrder.id,
                    companyNeedId: companyNeedId.value,
                    workers: eligibleWorkers.map(mapMoveWorkerToMissionBulkWorker),
                    continueIfMissionUpdateFails: true,
                });

                if (!isMissionUpdateSuccessful) {
                    toastError(t("modals.companyNeed.moveWorker.missionConfirm.error"));
                    return;
                }

                showAlreadyAssignedToast(alreadyAssignedCount, eligibleWorkers.length, MissionBulkWorkerStatus.Proposed);

                applyOptimisticMoveFromJobOffer();
                props.onSuccess?.(MoveWorkerCompanyNeedCommands.MOVE_TO_MISSION_PROPOSED);
            } catch {
                toastError(t("modals.companyNeed.moveWorker.missionConfirm.error"));
            }
        },
    });

    emit("close");
};

const applyOptimisticMatchingRecruitmentMove = (
    workers: MoveWorker[],
    status: CompanyNeedWorkerStatus,
    response: AddWorkersToCompanyNeedResponse
) => {
    if (!props.isFromMatching) return;
    companyNeedStore.applyOptimisticMatchingWorkersToRecruitment(workers, status, response.created);
};

const handleUpdateProfilesStatus = async (workerIds: string[], status: MoveWorkerCompanyNeedCommands) => {
    const response = await executeWithFeedback(
        () => companyNeedService.updateProfilesStatus(companyNeedId.value, workerIds, status),
        t("modals.companyNeed.moveWorker.updateProfilesStatus.success", { count: workerIds.length }),
        t("modals.companyNeed.moveWorker.updateProfilesStatus.error", { count: workerIds.length })
    );
    return response !== null;
};

const handleAddWorkersToCompanyNeed = async (status: CompanyNeedWorkerStatus, workers: MoveWorker[] = props.workers) => {
    const response = await executeWithFeedback(
        () =>
            companyNeedService.addWorkersToCompanyNeed(companyNeedId.value, {
                workers: workers.map(mapMoveWorkerToWorkerToAdd),
                status,
            }),
        t("modals.companyNeed.moveWorker.addWorkersToCompanyNeed.success", { count: workers.length }),
        t("modals.companyNeed.moveWorker.addWorkersToCompanyNeed.error", { count: workers.length })
    );
    if (!response) return null;

    applyOptimisticMatchingRecruitmentMove(workers, status, response);
    refreshMatchingIfCurrentPageEmpty();
    companyNeedStore.fetchCompanyNeed();
    return response;
};

const handleMoveToPrequalified = async () => {
    let isSuccessful = false;
    if (shouldAddProfilesToTargetNeed.value) {
        isSuccessful = (await handleAddWorkersToCompanyNeed(CompanyNeedWorkerStatus.PREQUALIFIED)) !== null;
    } else {
        isSuccessful =
            (await executeWithFeedback(
                () =>
                    Promise.all(
                        props.workers.map((worker) =>
                            companyNeedService.updateCompanyNeedWorkerStatus(companyNeedId.value, worker.id, {
                                status: CompanyNeedWorkerStatus.PREQUALIFIED,
                            })
                        )
                    ),
                t("modals.companyNeed.moveWorker.updateProfilesStatus.success", { count: props.workers.length }),
                t("modals.companyNeed.moveWorker.updateProfilesStatus.error", { count: props.workers.length })
            )) !== null;
    }
    if (!isSuccessful) return;

    applyOptimisticMoveFromJobOffer();
    props.onSuccess?.(MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED);
    companyNeedStore.fetchRecruitmentProfiles();
    emit("close");
};

const handleMoveToContacted = () => {
    const workersNeverContacted = props.workers.filter((worker) => !worker.history?.some((h) => h.type === HistoryType.CONTACTED));

    const applyContactedStatus = async () => {
        let isSuccessful = false;
        if (shouldAddProfilesToTargetNeed.value) {
            isSuccessful = (await handleAddWorkersToCompanyNeed(CompanyNeedWorkerStatus.CONTACTED)) !== null;
        } else {
            const workerIds = props.workers.map((worker) => worker.id);
            isSuccessful = await handleUpdateProfilesStatus(workerIds, MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED);
        }
        if (!isSuccessful) return;

        applyOptimisticMoveFromJobOffer();
        props.onSuccess?.(MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED);
        companyNeedStore.fetchRecruitmentProfiles();
        emit("close");
    };

    if (workersNeverContacted.length > 0) {
        const workerIds = workersNeverContacted.map((worker) => worker.id).filter((id): id is string => Boolean(id));
        openLastContactModalForCompanyNeed({
            workerIds,
            companyNeedId: companyNeedId.value,
            companyNeedCreatedAt: companyNeedStore.companyNeed?.createdAt ?? undefined,
            onSuccess: applyContactedStatus,
        });
        return;
    }

    applyContactedStatus();
};

const applyCreatedInterview = (workerId: string, payload?: ScheduleInterviewSuccessPayload) => {
    if (!payload) return;
    companyNeedStore.applyRescheduledInterview(workerId, mapScheduleInterviewPayloadToWorkerInterview(payload));
};

const handleMoveToInterview = (historyType: HistoryType, mode: ScheduleInterviewType, command: MoveWorkerCompanyNeedCommands) => {
    const eligibleWorkers = workersWithEmail.value;
    if (eligibleWorkers.length !== 1) {
        emit("close");
        return;
    }

    const worker = eligibleWorkers[0];
    const isAlreadyInInterviewColumn = worker.currentInterview?.mode === mode && worker.history?.some((entry) => entry.type === historyType);

    const applyInterviewStatus = async (payload?: ScheduleInterviewSuccessPayload) => {
        applyCreatedInterview(worker.id, payload);

        let isSuccessful = false;
        if (props.isFromMatching) {
            const status =
                command === MoveWorkerCompanyNeedCommands.MOVE_TO_INTERVIEW
                    ? CompanyNeedWorkerStatus.INTERVIEW
                    : CompanyNeedWorkerStatus.COMPANY_INTERVIEW;
            isSuccessful = (await handleAddWorkersToCompanyNeed(status, eligibleWorkers)) !== null;
        } else {
            isSuccessful = await handleUpdateProfilesStatus([worker.id], command);
        }
        if (!isSuccessful) return;

        applyOptimisticMoveFromJobOffer();
        props.onSuccess?.(command);
        companyNeedStore.fetchRecruitmentProfiles();
        emit("close");
    };

    const handleAdditionalInterview = (payload?: ScheduleInterviewSuccessPayload) => {
        applyCreatedInterview(worker.id, payload);
        toastSuccess(t("modals.companyNeed.scheduleInterview.createSuccess"));
        emit("close");
    };

    openScheduleInterviewModal(
        { worker, companyNeedId: companyNeedId.value, mode, companyId: props.companyId },
        isAlreadyInInterviewColumn ? handleAdditionalInterview : applyInterviewStatus
    );
};

const handleMoveToCompanyProposed = () => {
    const eligibleWorkers = workersWithEmail.value;
    if (eligibleWorkers.length === 0) return;

    const workerId = eligibleWorkers[0].id;
    modalStore.actions.open(ModalName.SendActiveProposalModal, {
        besoinId: companyNeedId.value,
        workerId: workerId,
        jobId: companyNeedStore.companyNeed?.jobId,
        companyId: companyNeedStore.companyNeed?.companyId,
        onRefresh: async () => {
            const response = await handleAddWorkersToCompanyNeed(CompanyNeedWorkerStatus.COMPANY_PROPOSED, eligibleWorkers);
            if (!response) return;

            applyOptimisticMoveFromJobOffer();
            props.onSuccess?.(MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_PROPOSED);
            await companyNeedStore.fetchRecruitmentProfiles();
        },
    });
    emit("close");
};

const handleMoveToPool = async () => {
    const response = await executeWithFeedback(
        () =>
            companyNeedService.moveWorkersToPool(companyNeedId.value, {
                workers: props.workers.map(mapMoveWorkerToWorkerToMoveToPool),
            }),
        t("modals.companyNeed.moveWorker.moveWorkersToPool.success", { count: props.workers.length }),
        t("modals.companyNeed.moveWorker.moveWorkersToPool.error", { count: props.workers.length })
    );
    if (response === null) {
        emit("close");
        return;
    }

    applyOptimisticMoveFromJobOffer();
    if (props.isFromRecruitment) {
        handleMoveToPoolFromRecruitment(props.workers.map((worker) => worker.id));
    }
    if (props.isFromMatching && !props.isFromRecruitment) {
        handleMoveToPoolFromMatching(props.workers.map((worker) => worker.id));
        // Skip full reload only when the current page still has rows; an empty page
        // with remaining total must refetch to show the next profiles or empty state.
        if (companyNeedStore.visibleMatchingWorkers.items.length > 0 && !isJobOfferMove()) {
            emit("close");
            return;
        }
    }
    reloadAfterPoolMove({ fromRecruitment: props.isFromRecruitment });
    emit("close");
};

const handleExecuteCommand = (command: MoveWorkerCompanyNeedCommands) => {
    if (COMMANDS_REQUIRING_EMAIL.includes(command) && workersWithEmail.value.length === 0) {
        toastError(t("modals.companyNeed.moveWorker.workerEmailRequired", props.workers.length));
        return;
    }

    switch (command) {
        case MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED:
            handleMoveToPrequalified();
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED:
            handleMoveToContacted();
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_INTERVIEW:
            handleMoveToInterview(HistoryType.INTERVIEW, ScheduleInterviewType.AGENCY, command);
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_PROPOSED:
            handleMoveToCompanyProposed();
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_INTERVIEW:
            handleMoveToInterview(HistoryType.COMPANY_INTERVIEW, ScheduleInterviewType.CLIENT, command);
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_POOL:
            handleMoveToPool();
            return;
        case MoveWorkerCompanyNeedCommands.ADD_TO_ANOTHER_MISSION:
            currentView.value = MoveWorkerModalView.MISSIONS;
            return;
        case MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER:
            handleOpenManageOrderModal();
            return;
        case MoveWorkerCompanyNeedCommands.MOVE_TO_ANOTHER_COMPANY_NEED:
            currentView.value = MoveWorkerModalView.COMPANY_NEEDS;
            return;
        case MoveWorkerCompanyNeedCommands.ADD_TO_OTHER_POOLS:
            search.value = "";
            currentView.value = MoveWorkerModalView.ADD_TO_OTHER_POOLS;
            return;
    }
};

const handleAddToOtherPool = async (need: CompanyNeedItem) => {
    if (actionProgress.value.alreadyCompanyNeedIds.includes(need.id) || actionProgress.value.inProgressCompanyNeedId) return;

    actionProgress.value = {
        ...actionProgress.value,
        inProgressCompanyNeedId: need.id,
    };
    try {
        await companyNeedService.moveWorkersToPool(need.id, {
            workers: props.workers.map(mapMoveWorkerToWorkerToMoveToPool),
        });
        actionProgress.value = {
            alreadyCompanyNeedIds: [...actionProgress.value.alreadyCompanyNeedIds, need.id],
            inProgressCompanyNeedId: null,
        };
        toastSuccess(
            t("modals.companyNeed.moveWorker.addToOtherPools.success", {
                count: props.workers.length,
                needName: need.name,
            })
        );
    } catch (error) {
        toastError(resolveMoveWorkerErrorMessage(error, "modals.companyNeed.moveWorker.addToOtherPools.error"));
        actionProgress.value = {
            ...actionProgress.value,
            inProgressCompanyNeedId: null,
        };
    }
};

const handleSelectCompanyNeed = (need: CompanyNeedItem) => {
    search.value = "";
    selectedCompanyNeedId.value = need.id;
    selectedNeedName.value = need.name;
    selectedNeedFromJobOffer.value = need.fromJobOffer ?? {};
    currentView.value = MoveWorkerModalView.MOVE_TO_ANOTHER_COMPANY_NEED;
};

const handleSelectMission = (command: MoveWorkerCommand | null, index: number) => {
    selectedMissionCommand.value = command;
    selectedItemIndex.value = index;
};

type ToastMessageKeys = {
    single: string;
    partial: string;
    all: string;
};

const REGISTRATION_NOT_COMPLETE_KEYS: ToastMessageKeys = {
    single: "modals.missionDetails.errors.workerRegistrationNotComplete.single",
    partial: "modals.missionDetails.errors.workerRegistrationNotComplete.partial",
    all: "modals.missionDetails.errors.workerRegistrationNotComplete.all",
};

const ALREADY_ASSIGNED_KEYS: ToastMessageKeys = {
    single: "modals.companyNeed.moveWorker.missionConfirm.workerAlreadyAssigned.single",
    partial: "modals.companyNeed.moveWorker.missionConfirm.workerAlreadyAssigned.partial",
    all: "modals.companyNeed.moveWorker.missionConfirm.workerAlreadyAssigned.all",
};

const buildPluralizedMessage = (count: number, total: number, keys: ToastMessageKeys, params: Record<string, unknown> = {}): string | null => {
    if (count === 0) return null;
    if (count === 1) return t(keys.single, params);
    if (count < total) return t(keys.partial, { count, total, ...params }, count);
    return t(keys.all, params);
};

const showRegistrationNotCompleteToast = (notCompleteCount: number, totalCount: number) => {
    const message = buildPluralizedMessage(notCompleteCount, totalCount, REGISTRATION_NOT_COMPLETE_KEYS);
    if (message) toastError(message);
};

const showAlreadyAssignedToast = (alreadyAssignedCount: number, totalCount: number, status: MissionBulkWorkerStatus) => {
    const action =
        status === MissionBulkWorkerStatus.Confirmed
            ? t("modals.companyNeed.moveWorker.missionConfirm.workerAlreadyAssignedAction.confirmed")
            : t("modals.companyNeed.moveWorker.missionConfirm.workerAlreadyAssignedAction.proposed");

    const message = buildPluralizedMessage(alreadyAssignedCount, totalCount, ALREADY_ASSIGNED_KEYS, { action });
    if (message) toastError(message);
};

const isPartiallyAssigned = (alreadyAssignedCount: number, totalCount: number) => alreadyAssignedCount > 0 && alreadyAssignedCount < totalCount;

const handleMissionAction = async (missionStatus: MissionBulkWorkerStatus) => {
    if (!selectedMissionCommand.value) return;

    const eligibleWorkers = workersWithEmail.value;
    if (eligibleWorkers.length === 0) return;

    const missionId = selectedMissionCommand.value.key;
    const workers = eligibleWorkers.map(mapMoveWorkerToMissionBulkWorker);

    try {
        const results = await missionService.addWorkersToMissionBulk(missionId, {
            status: missionStatus,
            workers,
        });

        if (results.some((item) => item.code === MISSION_MAX_WORKERS_REACHED_CODE)) {
            modalStore.actions.open(ModalName.TooMuchWorkerConfirm);
            return;
        }

        // Assigning a worker already on the mission should not be possible through the UI, but the API
        // can still return this code if it happens (e.g. race condition), so we handle it gracefully.
        const alreadyAssignedCount = results.filter((item) => item.code === MISSION_WORKER_ALREADY_ASSIGNED_CODE).length;
        const registrationNotCompleteCount = results.filter((item) => item.code === MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE).length;

        const successfulCount = results.filter((item) => item.success).length;
        if (successfulCount > 0) {
            if (isPartiallyAssigned(alreadyAssignedCount, workers.length)) {
                const partialKey =
                    missionStatus === MissionBulkWorkerStatus.Confirmed
                        ? "modals.companyNeed.moveWorker.missionConfirm.successPartialConfirmed"
                        : "modals.companyNeed.moveWorker.missionConfirm.successPartialProposed";
                toastSuccess(t(partialKey, { count: successfulCount, total: workers.length }));
            } else {
                showAlreadyAssignedToast(alreadyAssignedCount, workers.length, missionStatus);

                const successKey =
                    missionStatus === MissionBulkWorkerStatus.Confirmed
                        ? "modals.companyNeed.moveWorker.missionConfirm.successConfirmed"
                        : "modals.companyNeed.moveWorker.missionConfirm.successProposed";
                toastSuccess(t(successKey));
            }
        } else {
            showAlreadyAssignedToast(alreadyAssignedCount, workers.length, missionStatus);
        }

        showRegistrationNotCompleteToast(registrationNotCompleteCount, workers.length);

        emit("close");
        if (successfulCount > 0) {
            applyOptimisticMoveFromJobOffer();
            props.onSuccess?.(MoveWorkerCompanyNeedCommands.MOVE_TO_MISSION_PROPOSED);
            companyNeedStore.reloadData(["missions", "recruitment"]);
        } else {
            props.onCancel?.();
        }
    } catch (error) {
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        if ((error as any)?.response?.data?.code === MISSION_MAX_WORKERS_REACHED_CODE) {
            modalStore.actions.open(ModalName.TooMuchWorkerConfirm);
            return;
        }
        toastError(t("modals.companyNeed.moveWorker.missionConfirm.error"));
    }
};
</script>

<template>
    <Modal
        class="move-worker-company-need-modal"
        v-bind="$attrs"
        position="top"
        width="720px"
        :open="open"
        no-padding
        no-padding-header
        no-padding-footer
        @close="handleClose"
    >
        <template #header>
            <div class="move-worker-company-need-modal__header-actions">
                <QuickActionBadge :label="label" />
                <QuickActionSearch
                    :is-focused="open"
                    v-model="search"
                    :placeholder="searchPlaceholder"
                    :icon="searchIcon"
                    @clear="search = ''"
                    @keydown="handleKeyDown"
                />
            </div>
        </template>
        <template #default>
            <div class="move-worker-company-need-modal__content">
                <div class="move-worker-company-need-modal__content-items">
                    <template v-if="currentView === MoveWorkerModalView.MOVE || currentView === MoveWorkerModalView.MOVE_TO_ANOTHER_COMPANY_NEED">
                        <MoveQuickActionItem
                            ref="moveQuickActionItemRef"
                            :workers="props.workers"
                            :company-need-id="companyNeedId"
                            :hide-move-to-another-need="!!selectedCompanyNeedId"
                            :search="search"
                            :selected-index="selectedItemIndex"
                            @click="handleExecuteCommand"
                            @update:itemCount="updateItemCount"
                        />
                    </template>
                    <template v-else-if="currentView === MoveWorkerModalView.MISSIONS">
                        <MissionsQuickActionItem
                            ref="missionsQuickActionItemRef"
                            :workers="props.workers"
                            :selected-index="selectedItemIndex"
                            :company-need-id="companyNeedId"
                            @select="handleSelectMission"
                            @create-order="handleOpenManageOrderModal"
                            @update:itemCount="updateItemCount"
                        />
                    </template>
                    <template v-else-if="currentView === MoveWorkerModalView.COMPANY_NEEDS || isAddToOtherPoolsView">
                        <CompanyNeedsQuickActionItem
                            ref="companyNeedsQuickActionItemRef"
                            :workers="props.workers"
                            :company-need-id="companyNeedId"
                            :search="search"
                            :selected-index="selectedItemIndex"
                            :show-action="isAddToOtherPoolsView"
                            :action-progress="actionProgress"
                            @select="handleSelectCompanyNeed"
                            @add="handleAddToOtherPool"
                            @update:itemCount="updateItemCount"
                        />
                    </template>
                </div>
            </div>
        </template>
        <template v-if="isFooterVisible" #footer>
            <div class="move-worker-company-need-modal__footer">
                <ButtonAlt
                    :label="t('modals.companyNeed.moveWorker.proposeMission')"
                    variant="stroke"
                    type="neutral"
                    size="sm"
                    left-icon="arrow-right"
                    @click="handleMissionAction(MissionBulkWorkerStatus.Proposed)"
                />
                <ButtonAlt
                    :label="t('modals.companyNeed.moveWorker.confirmMission')"
                    variant="stroke"
                    type="neutral"
                    size="sm"
                    left-icon="check"
                    @click="handleMissionAction(MissionBulkWorkerStatus.Confirmed)"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.move-worker-company-need-modal {
    &__header-actions {
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
        display: flex;
        flex-direction: column;
        align-items: start;
        width: 100%;
    }

    &__content {
        padding: 0 var(--spacing-md, 8px);

        &-items {
            display: flex;
            padding: var(--spacing-xs, 4px) var(--spacing-md, 8px) var(--spacing-md, 8px) var(--spacing-xxs, 2px);
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-xxs, 2px);
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-md, 8px);
        height: 48px;
        align-items: center;
        padding: 0 var(--spacing-lg, 12px);
    }
}
</style>
